/**
 * Registration Page
 * SCR-801: Registration & OAuth
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useRegister, useVerifyEmail, useResendVerification } from '../hooks/useAuth';
import type { RegisterRequest } from '../types';
import { PASSWORD_REQUIREMENTS } from '../types';
import { featureFlags } from '@/config/featureFlags';

export function RegistrationPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'register' | 'verify'>('register');
  const [email, setEmail] = useState('');
  const [formData, setFormData] = useState<Partial<RegisterRequest>>({
    acceptTerms: false,
    acceptPrivacy: false,
    marketingConsent: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');

  const registerMutation = useRegister();
  const verifyMutation = useVerifyEmail();
  const resendMutation = useResendVerification();

  const handleOAuthLogin = (provider: 'google' | 'digid') => {
    window.location.href = `/api/v1/auth/oauth/${provider}`;
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result = await registerMutation.mutateAsync(formData as RegisterRequest);
      setEmail(result.email);
      if (result.requiresVerification) {
        setStep('verify');
      } else {
        navigate('/onboarding');
      }
    } catch (error) {
      console.error('Registration failed:', error);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result = await verifyMutation.mutateAsync({ verificationCode });
      if (result.success) {
        navigate(result.redirectUrl || '/onboarding');
      }
    } catch (error) {
      console.error('Verification failed:', error);
    }
  };

  const handleResend = async () => {
    try {
      await resendMutation.mutateAsync({ email });
    } catch (error) {
      console.error('Resend failed:', error);
    }
  };

  const getPasswordStrength = (password: string) => {
    let score = 0;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[!@#$%^&*]/.test(password)) score++;
    return score;
  };

  if (step === 'verify') {
    return (
      <div className="min-h-screen bg-mv-gray-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-mv-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-mv-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-mv-gray-900 mb-2">Verifieer je e-mailadres</h1>
            <p className="text-mv-gray-600">
              We hebben een verificatiecode gestuurd naar <strong>{email}</strong>
            </p>
          </div>

          <form onSubmit={handleVerify} className="bg-white rounded-lg border border-mv-gray-200 p-8">
            <div className="mb-6">
              <label htmlFor="code" className="block text-sm font-medium text-mv-gray-700 mb-2">
                Verificatiecode
              </label>
              <input
                type="text"
                id="code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="123456"
                maxLength={6}
                className="w-full px-4 py-3 text-center text-2xl tracking-widest border border-mv-gray-300 rounded-lg focus:ring-2 focus:ring-mv-blue-600 focus:border-transparent"
                autoFocus
              />
            </div>

            <button
              type="submit"
              disabled={verificationCode.length !== 6 || verifyMutation.isPending}
              className="w-full py-3 bg-mv-blue-600 text-white font-semibold rounded-lg hover:bg-mv-blue-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {verifyMutation.isPending ? 'Verifiëren...' : 'Verifiëren'}
            </button>

            <div className="mt-6 text-center">
              <p className="text-sm text-mv-gray-600 mb-2">Geen code ontvangen?</p>
              <button
                type="button"
                onClick={handleResend}
                disabled={resendMutation.isPending}
                className="text-sm text-mv-blue-600 hover:text-mv-blue-900 font-medium"
              >
                {resendMutation.isPending ? 'Versturen...' : 'Opnieuw versturen'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-mv-gray-100 flex">
      {/* Left Panel - Hero */}
      <div className="hidden lg:flex lg:w-1/2 bg-mv-blue-900 text-white p-12 flex-col justify-center">
        <div className="max-w-md">
          <h1 className="text-4xl font-bold mb-6">Welkom bij myVault</h1>
          <p className="text-xl text-blue-100 mb-8">
            Jouw persoonlijke kluis voor belangrijke documenten, powered by AI.
          </p>
          <ul className="space-y-4">
            <li className="flex items-center gap-3">
              <svg className="w-6 h-6 text-mv-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Veilige opslag voor al je documenten</span>
            </li>
            <li className="flex items-center gap-3">
              <svg className="w-6 h-6 text-mv-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>AI-samenvattingen in begrijpelijke taal</span>
            </li>
            <li className="flex items-center gap-3">
              <svg className="w-6 h-6 text-mv-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Deel veilig met familie en professionals</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-mv-gray-900 mb-2">Account aanmaken</h2>
            <p className="text-mv-gray-600">
              Heb je al een account?{' '}
              <Link to="/login" className="text-mv-blue-600 hover:text-mv-blue-900 font-medium">
                Log in
              </Link>
            </p>
          </div>

          {/* OAuth Buttons */}
          <div className="space-y-3 mb-6">
            <button
              type="button"
              onClick={() => handleOAuthLogin('google')}
              className="w-full flex items-center justify-center gap-3 py-3 px-4 border-2 border-mv-gray-300 rounded-lg hover:border-mv-blue-600 transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="font-medium text-mv-gray-700">Doorgaan met Google</span>
            </button>

            {/* DigiD authentication - controlled by VITE_FEATURE_DIGID env var */}
            {featureFlags.DIGID_ENABLED && (
              <button
                type="button"
                onClick={() => handleOAuthLogin('digid')}
                className="w-full flex items-center justify-center gap-3 py-3 px-4 border-2 border-mv-gray-300 rounded-lg hover:border-mv-blue-600 transition-colors"
              >
                <span className="w-5 h-5 bg-orange-500 rounded text-white text-xs font-bold flex items-center justify-center">D</span>
                <span className="font-medium text-mv-gray-700">Doorgaan met DigiD</span>
              </button>
            )}
          </div>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-mv-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-mv-gray-100 text-mv-gray-500">of</span>
            </div>
          </div>

          {/* Registration Form */}
          <form onSubmit={handleRegister} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-mv-gray-700 mb-1">
                  Voornaam
                </label>
                <input
                  type="text"
                  id="firstName"
                  value={formData.firstName || ''}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="w-full px-4 py-2 border border-mv-gray-300 rounded-lg focus:ring-2 focus:ring-mv-blue-600 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-mv-gray-700 mb-1">
                  Achternaam
                </label>
                <input
                  type="text"
                  id="lastName"
                  value={formData.lastName || ''}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="w-full px-4 py-2 border border-mv-gray-300 rounded-lg focus:ring-2 focus:ring-mv-blue-600 focus:border-transparent"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-mv-gray-700 mb-1">
                E-mailadres
              </label>
              <input
                type="email"
                id="email"
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 border border-mv-gray-300 rounded-lg focus:ring-2 focus:ring-mv-blue-600 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-mv-gray-700 mb-1">
                Wachtwoord
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={formData.password || ''}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-2 pr-10 border border-mv-gray-300 rounded-lg focus:ring-2 focus:ring-mv-blue-600 focus:border-transparent"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-mv-gray-400 hover:text-mv-gray-600"
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
              {formData.password && (
                <div className="mt-2">
                  <div className="flex gap-1 mb-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded ${
                          i <= getPasswordStrength(formData.password || '')
                            ? 'bg-green-500'
                            : 'bg-mv-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-mv-gray-500">
                    {getPasswordStrength(formData.password || '') < 3
                      ? 'Zwak wachtwoord'
                      : getPasswordStrength(formData.password || '') < 5
                      ? 'Redelijk wachtwoord'
                      : 'Sterk wachtwoord'}
                  </p>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="passwordConfirm" className="block text-sm font-medium text-mv-gray-700 mb-1">
                Bevestig wachtwoord
              </label>
              <input
                type="password"
                id="passwordConfirm"
                value={formData.passwordConfirm || ''}
                onChange={(e) => setFormData({ ...formData, passwordConfirm: e.target.value })}
                className="w-full px-4 py-2 border border-mv-gray-300 rounded-lg focus:ring-2 focus:ring-mv-blue-600 focus:border-transparent"
                required
              />
            </div>

            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.acceptTerms || false}
                  onChange={(e) => setFormData({ ...formData, acceptTerms: e.target.checked })}
                  className="mt-1 h-4 w-4 text-mv-blue-600 border-mv-gray-300 rounded focus:ring-mv-blue-600"
                  required
                />
                <span className="text-sm text-mv-gray-600">
                  Ik ga akkoord met de{' '}
                  <Link to="/terms" className="text-mv-blue-600 hover:underline">
                    algemene voorwaarden
                  </Link>
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.acceptPrivacy || false}
                  onChange={(e) => setFormData({ ...formData, acceptPrivacy: e.target.checked })}
                  className="mt-1 h-4 w-4 text-mv-blue-600 border-mv-gray-300 rounded focus:ring-mv-blue-600"
                  required
                />
                <span className="text-sm text-mv-gray-600">
                  Ik ga akkoord met het{' '}
                  <Link to="/privacy" className="text-mv-blue-600 hover:underline">
                    privacybeleid
                  </Link>
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.marketingConsent || false}
                  onChange={(e) => setFormData({ ...formData, marketingConsent: e.target.checked })}
                  className="mt-1 h-4 w-4 text-mv-blue-600 border-mv-gray-300 rounded focus:ring-mv-blue-600"
                />
                <span className="text-sm text-mv-gray-600">
                  Ik wil graag updates en tips ontvangen (optioneel)
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={!formData.acceptTerms || !formData.acceptPrivacy || registerMutation.isPending}
              className="w-full py-3 bg-mv-blue-600 text-white font-semibold rounded-lg hover:bg-mv-blue-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {registerMutation.isPending ? 'Account aanmaken...' : 'Account aanmaken'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
